import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './AddService.css';
import { api, ENDPOINTS } from '../../services/apiConfig';

const AddService = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const totalSteps = 3;
  
  // State for multiple images
  const [selectedFiles, setSelectedFiles] = useState({
    main: null,
    second: null,
    third: null
  });
  
  const [previewUrls, setPreviewUrls] = useState({
    main: '',
    second: '',
    third: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    availability: '',
    category: '',
    duration: '',
    maxParticipants: '',
    requiresBooking: '',
    tags: '',
    termsAndConditions: '',
    contactInfo: '',
    description: '',
    amount: ''
  });

  useEffect(() => {
    // Check for JWT token
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      navigate('/login');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e, imagePosition) => {
    const file = e.target.files[0];
    if (file) {
      // Update the selected file state
      setSelectedFiles(prev => ({
        ...prev,
        [imagePosition]: file
      }));
      
      // Create a preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrls(prev => ({
          ...prev,
          [imagePosition]: fileReader.result
        }));
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === totalSteps && !acceptTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    if (!selectedFiles.main) {
      toast.error('Please upload at least the main image for your service');
      return;
    }
    
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token missing');
        navigate('/login');
        return;
      }

      // Create form data object for multipart/form-data
      const serviceData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        serviceData.append(key, formData[key]);
      });
      
      // Append files if selected
      if (selectedFiles.main) {
        serviceData.append('serviceImages', selectedFiles.main);
      }
      
      if (selectedFiles.second) {
        serviceData.append('serviceImages', selectedFiles.second);
      }
      
      if (selectedFiles.third) {
        serviceData.append('serviceImages', selectedFiles.third);
      }

      // Make API request
      const response = await api.service.post(
        ENDPOINTS.service.addserviceinfo,
        serviceData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success('Service added successfully!');
        navigate('/d/vendor-dashboard');
      }
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(error.response?.data?.message || 'Failed to add service');
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="stepper-container">
        {[...Array(totalSteps)].map((_, index) => (
          <div key={index} className="step-item">
            <div 
              className={`step-number ${currentStep > index+1 ? "completed" : ""} ${currentStep === index+1 ? "active" : ""}`}
              onClick={() => setCurrentStep(index+1)}
            >
              {currentStep > index+1 ? "✓" : index+1}
            </div>
            <div className="step-label">
              {index === 0 ? "Basic Info" : index === 1 ? "Details" : "Media & Terms"}
            </div>
            {index < totalSteps - 1 && <div className={`step-line ${currentStep > index+1 ? "completed" : ""}`}></div>}
          </div>
        ))}
      </div>
    );
  };

  const openTermsAndConditions = () => {
    // This would open the terms and conditions PDF in a new tab
    window.open('/assets/Vendor Agreement and Terms of Engagement.pdf', '_blank');
  };

  return (
    <div className="add-service-container">
      <div className="add-service-header">
        <h1>Add New Service</h1>
        <p>Enter service details below to create a new offering for your customers</p>
      </div>

      {renderStepIndicator()}

      <form onSubmit={handleSubmit} className="add-service-form">
        {currentStep === 1 && (
          <div className="form-step">
            <h2>Basic Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Service Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Cove Event"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Event Center">Event Center</option>
                  <option value="Catering">Catering</option>
                  <option value="Photography">Photography</option>
                  <option value="Decoration">Decoration</option>
                  <option value="Entertainment">Entertainment</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="availability">Availability</label>
                <select
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Available">Available</option>
                  <option value="Coming Soon">Coming Soon</option>
                  <option value="Unavailable">Unavailable</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tags">Tags</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="e.g. Affordable, Luxury, Family-friendly"
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="description">Service Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your service in detail"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="form-step">
            <h2>Service Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="duration">Duration (minutes)</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxParticipants">Maximum Participants</label>
                <input
                  type="number"
                  id="maxParticipants"
                  name="maxParticipants"
                  value={formData.maxParticipants}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="requiresBooking">Requires Booking</label>
                <select
                  id="requiresBooking"
                  name="requiresBooking"
                  value={formData.requiresBooking}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="amount">Service Cost (GHS)</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="e.g. 300"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="contactInfo">Contact Information</label>
                <input
                  type="text"
                  id="contactInfo"
                  name="contactInfo"
                  value={formData.contactInfo}
                  onChange={handleInputChange}
                  placeholder="e.g. +23324543434"
                />
              </div>
              
              <div className="form-group terms-group full-width">
                <label htmlFor="termsAndConditions">Payment Terms</label>
                <textarea
                  id="termsAndConditions"
                  name="termsAndConditions"
                  value={formData.termsAndConditions}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter payment terms and conditions"
                  required
                ></textarea>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="form-step">
            <h2>Media & Terms</h2>
            <div className="form-grid">
              <div className="form-group file-upload-section full-width">
                <h3>Service Images (Upload up to 3 images)</h3>
                <p className="upload-instruction">The first image will be used as the main display image</p>
                
                {/* Main Image Upload */}
                <div className="image-upload-container">
                  <div className="file-upload-group">
                    <label htmlFor="mainImage">Main Image (Required)</label>
                    <div className="file-upload-container">
                      <input
                        type="file"
                        id="mainImage"
                        name="mainImage"
                        onChange={(e) => handleFileChange(e, 'main')}
                        accept="image/*"
                        className="file-input"
                        required
                      />
                      <div className="file-upload-btn">
                        <span>Choose File</span>
                      </div>
                      <div className="file-name">
                        {selectedFiles.main ? selectedFiles.main.name : 'No file chosen'}
                      </div>
                    </div>
                    {previewUrls.main && (
                      <div className="image-preview">
                        <img src={previewUrls.main} alt="Main service preview" />
                      </div>
                    )}
                  </div>
                  
                  {/* Second Image Upload */}
                  <div className="file-upload-group">
                    <label htmlFor="secondImage">Second Image (Optional)</label>
                    <div className="file-upload-container">
                      <input
                        type="file"
                        id="secondImage"
                        name="secondImage"
                        onChange={(e) => handleFileChange(e, 'second')}
                        accept="image/*"
                        className="file-input"
                      />
                      <div className="file-upload-btn">
                        <span>Choose File</span>
                      </div>
                      <div className="file-name">
                        {selectedFiles.second ? selectedFiles.second.name : 'No file chosen'}
                      </div>
                    </div>
                    {previewUrls.second && (
                      <div className="image-preview">
                        <img src={previewUrls.second} alt="Second service preview" />
                      </div>
                    )}
                  </div>
                  
                  {/* Third Image Upload */}
                  <div className="file-upload-group">
                    <label htmlFor="thirdImage">Third Image (Optional)</label>
                    <div className="file-upload-container">
                      <input
                        type="file"
                        id="thirdImage"
                        name="thirdImage"
                        onChange={(e) => handleFileChange(e, 'third')}
                        accept="image/*"
                        className="file-input"
                      />
                      <div className="file-upload-btn">
                        <span>Choose File</span>
                      </div>
                      <div className="file-name">
                        {selectedFiles.third ? selectedFiles.third.name : 'No file chosen'}
                      </div>
                    </div>
                    {previewUrls.third && (
                      <div className="image-preview">
                        <img src={previewUrls.third} alt="Third service preview" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-group terms-checkbox-group full-width">
                <div className="terms-consent">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                  />
                  <label htmlFor="acceptTerms">
                    I agree to the <span className="terms-link" onClick={openTermsAndConditions}>Terms and Conditions</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          {currentStep > 1 && (
            <button 
              type="button" 
              className="prev-btn"
              onClick={prevStep}
            >
              Previous
            </button>
          )}
          
          {currentStep < totalSteps ? (
            <button 
              type="button" 
              className="next-btn"
              onClick={nextStep}
            >
              Next
            </button>
          ) : (
            <>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate('/d/vendor-dashboard')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isLoading}
              >
                {isLoading ? 'Adding Service...' : 'Add Service'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddService;